/** tm/legal/LegalTodaySummary.tsx — 「새 소식」 화면의 3열: 목록 카드를 되풀이하지 않고 「오늘 요약」만.
 *  묶음별 건수(누르면 그 구분으로) · 법령 변경(시행 D-n) · 국회 법안 상태 · 국무회의 의결 · 입법예고 의견제출 · 「모두 확인」 */
import type { ReactNode } from 'react';
import { C, T, R } from '../components/ui';
import { btn, num, secTitle, MUTED } from './legalTheme';
import { hookOf, hookColor, kindOf, type AlertEntry } from './alertUtil';
import { ItemTitle, itemTitleText } from './legalUi';
import type { AlertKey, FeedTab, ItemKind, LegalAlerts } from './legalTypes';

interface Props {
  data: LegalAlerts;
  onTab: (t: FeedTab) => void; onSelect: (kind: ItemKind, id: number) => void; onReadAll?: () => void;
}

const GROUPS: { key: AlertKey; label: string; tab: FeedTab }[] = [
  { key: 'law', label: '법령 개정', tab: 'law' }, { key: 'bill', label: '국회 법안', tab: 'bill' },
  { key: 'cabinet', label: '국무회의 의결', tab: 'cabinet' }, { key: 'notice', label: '입법예고', tab: 'notice' }, { key: 'legal', label: '판례·해석', tab: 'all' },
];

const Sec = ({ title, children }: { title: string; children: ReactNode }) => (
  <section style={{ marginBottom: 20, maxWidth: 820 }}>
    <div style={{ ...secTitle, fontSize: T.md, marginTop: 0 }}>{title}</div>
    {children}
  </section>
);

export default function LegalTodaySummary({ data, onTab, onSelect, onReadAll }: Props) {
  const count = (k: AlertKey) => { const g = data.groups.find(x => x.key === k); return g ? g.count ?? g.items.length : 0; };
  const items = (k: AlertKey): AlertEntry[] => (data.groups.find(x => x.key === k)?.items || []).map(it => ({ it, key: k }));
  const line = (e: AlertEntry) => (
    <button key={`${e.key}${e.it.id}`} type="button" className="lw-f lw-fi" onClick={() => onSelect(kindOf(e.key), e.it.id)} title={itemTitleText(e.it.title, e.it.custom_title)} style={{
      display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 4px', borderRadius: R.sm, cursor: 'pointer', fontFamily: 'inherit',
    }}>
      <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><ItemTitle title={e.it.title} custom={e.it.custom_title} /></span>
      <span style={{ display: 'block', fontSize: T.md, color: hookColor(e, C.accent, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {e.it.topic ? `${e.it.topic} · ` : ''}{hookOf(e)}
      </span>
    </button>
  );
  const laws = items('law'); const bills = items('bill'); const notices = items('notice'); const cabinets = items('cabinet');
  return (
    <div>
      <Sec title="오늘 요약">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
          {GROUPS.map(g => {
            const n = count(g.key);
            return (
              <button key={g.key} type="button" className="lw-f lw-card" disabled={!n} onClick={() => onTab(g.tab)} style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, cursor: n ? 'pointer' : 'default', fontFamily: 'inherit',
              }}>
                <span style={{ display: 'block', fontSize: T.md, color: C.sub }}>{g.label}</span>
                <b style={{ fontSize: T.xl, color: n ? C.text : MUTED, fontVariantNumeric: 'tabular-nums' }}>{num(n)}</b><span style={{ fontSize: T.md, color: C.sub }}>건</span>
              </button>
            );
          })}
        </div>
      </Sec>
      {laws.length > 0 && <Sec title="법령 변경">{laws.slice(0, 5).map(line)}</Sec>}
      {bills.length > 0 && <Sec title="국회 법안 · 상태 변경">{bills.slice(0, 5).map(line)}</Sec>}
      {cabinets.length > 0 && <Sec title="국무회의 의결">{cabinets.slice(0, 5).map(line)}</Sec>}
      {notices.length > 0 && <Sec title="입법예고 · 의견제출">{notices.slice(0, 3).map(line)}</Sec>}
      <p style={{ fontSize: T.md, color: MUTED, lineHeight: 1.6, maxWidth: 820, margin: '0 0 12px' }}>
        판례·해석 {num(count('legal'))}건은 가운데 목록에서 확인하세요. 뉴스는 알림 없이 주제별 「뉴스」에 쌓입니다{data.newsToday ? ` (오늘 ${num(data.newsToday)}건)` : ''}.
      </p>
      {onReadAll && data.total > 0 && <button type="button" className="lw-f" onClick={onReadAll} style={btn()}>모두 확인</button>}
    </div>
  );
}
